import { useEffect, useRef, useState, type DragEvent } from "react";

import LinkIcon from "@mui/icons-material/Link";
import { Box, Button, Skeleton } from "@mui/material";
// GCS, NOT Amplify. `aws-amplify`'s Storage authenticates with COGNITO
// credentials; g2 is a GCIP tenant, so there are none and every call threw
// "Error loading documents: Error: No credentials". A crawl of all 78 nav
// destinations found that on the Files/Documents tab of ELEVEN modules —
// Customer Quotes, Follow Ups, Sales Orders, Defect Reporting, Tasks, Work
// Orders, Supplier Quotes, Purchase Orders, Service Orders, Inventory/Tool
// Requests and Documents. One import, eleven broken tabs.
//
// src/storage/gcs.ts already existed for exactly this and says so in its own
// header: "Drop-in replacement for Amplify Storage… replace: import { Storage }
// from 'aws-amplify' with: import { GCSStorage as Storage }". It wraps the five
// /v6/storage/* routes over gs://aeronet-vault-dev. src/blank/tabs/BlankDocuments.tsx
// documents it as the canonical pattern. Vault simply never adopted it — a Rule 0
// miss whose blast radius was eleven modules.
//
// Safe on every tenant this tree serves: GCS_BUCKET_NAME=aeronet-vault-dev is set
// on the Cognito products too (verified live on aeronet-fast-api, -crm and -g2),
// and CLAUDE.md records the vault as the shared SCM+CRM Files-tab store. The
// .nz prod installs run a different frontend tree and are untouched by this.
import { GCSStorage as Storage } from "../storage/gcs";
import { UploaderEvents, Vault as VaultBase } from "dhx-vault-package";
import "dhx-vault-package/codebase/vault.css";
import { useSnackbar } from "notistack";


import AddLinkSheet from "../attachments/AddLinkSheet";
import { addLinkToRecord, droppedLink, linkRefusal } from "../attachments/addLink";
import { AttachmentService, objectsUnavailable } from "../attachments/AttachmentService";
import { attachmentForUpload } from "../attachments/storedFile";
import { escapeName, MISSING_SUFFIX, rowForFile, vaultRows, type VaultRow } from "../attachments/vaultRows";
import { DocumentAccess } from "../documents/types";
import { listToolbarButtonSx } from "./Buttons";

export function downloadBlob(blob, filename) {
  // from https://docs.amplify.aws/lib/storage/download/q/platform/js/#file-download-option
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  const clickHandler = () => {
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.removeEventListener("click", clickHandler);
    }, 150);
  };
  a.addEventListener("click", clickHandler, false);
  a.click();
  return a;
}

/** Why the server refused a file action, when it said (a 403 is a permission). */
export function uploadRefusal(e: unknown): string {
  const status = (e as { response?: { status?: number } })?.response?.status;
  return status === 403 ? ": you don't have permission to change this record's files" : "";
}

/** Why a registry call (finalize, remove) failed. They suppress the global toast, so the
 *  server's own words reach the user only through this: a permission, else its `detail`. */
function registryRefusal(e: unknown): string {
  const refusal = uploadRefusal(e);
  if (refusal) return refusal;
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" ? `: ${detail}` : "";
}

/** The Files tab names its record: list it from the database (attachments spec §5.3). */
function namesRecord(props: { source?: unknown; id?: unknown }): boolean {
  return Boolean(props.source) && props.id != null;
}

/** What a row that carries its raw name shows as TEXT: the name, and the missing suffix. */
function shownName(item: { fileName: string; missing?: boolean }): string {
  return item.missing ? item.fileName + MISSING_SUFFIX : item.fileName;
}

/**
 * DHX's list-mode row (vault.js listTemplate, the default mode) prints
 * `item.value + "." + item.extension`, which DHX's data init derives from `name` on parse/add
 * and never re-derives on update. So a link titled "Supplier portal" read ".Supplier portal",
 * and a just-uploaded row kept the raw picked name as HTML. For a row that carries its raw name
 * (`fileName`), this rebuilds the base template's name node as a text node of that name; the
 * rest of DHX's markup (icon, size, progress, buttons) is kept as built, except that an unknown
 * size reads UNKNOWN_SIZE instead of DHX's "0 B". Any other row, or
 * markup without the name node, is returned untouched.
 */
export function withListName(html: string, item: any): string {
  if (typeof item?.fileName !== "string") return html;
  const parsed = document.createElement("template");
  parsed.innerHTML = html;
  const nameNode = parsed.content.querySelector(".dhx_list-item--name");
  if (!nameNode) return html;
  // Keep only DHX's own progress figure (an in-progress row); drop the value.extension text
  // and anything a raw name may have put there.
  Array.from(nameNode.childNodes).forEach((node) => {
    if (!(node instanceof Element && node.classList.contains("dhx_item--progress-value"))) node.remove();
  });
  nameNode.insertBefore(document.createTextNode(shownName(item)), nameNode.firstChild);
  // DHX's data init turns an unset size into 0 and prints "0 B". A row with no recorded size
  // (vaultRows `sizeUnknown`) reads a dash; a known one, 0 included, keeps DHX's figure.
  const sizeNode = parsed.content.querySelector(".dhx_list-item--size");
  if (sizeNode && item.sizeUnknown === true) {
    sizeNode.textContent = UNKNOWN_SIZE;
  }
  return parsed.innerHTML;
}

/** What the list shows for a file whose size is not known. */
export const UNKNOWN_SIZE = "—";

/** A link opens only over http(s): the server accepts no other scheme, and this never trusts a
 *  stored value to be one it accepted. */
const OPENABLE_LINK = /^https?:\/\//i;

function Vault(props: any) {
  const vaultRef = useRef<HTMLDivElement | null>(null);
  const vaultInstanceRef = useRef<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  // The tab names its record: list it from the database — links and files in other stores
  // have nothing in the bucket to list. Without a record: exactly as before. Fixed at MOUNT,
  // as the DHX instance and its handlers below are built once: a consumer whose `source`
  // arrives after the first render (`config?.resource_type`) keeps the bucket behaviour for
  // that mount, and the Add link button follows the same decision.
  const [registry] = useState(() => namesRecord(props));

  useEffect(() => {
    if (!vaultRef.current) return;
    const host = vaultRef.current;

    if (props.access) {
      switch (props.access) {
        case DocumentAccess.Public:
          Storage.configure({
            customPrefix: { public: "storage/external/" },
          });
          break;
        case DocumentAccess.Protected:
          Storage.configure({ level: "protected" });
          break;
        case DocumentAccess.Private:
          Storage.configure({ level: "private" });
          break;
      }
    }

    // @ts-ignore
    const vault = new VaultBase(vaultRef.current, {
      data: props.data,
      mode: props.mode,
      uploader: {
        autosend: props.autosend,
        target: props.target,
      },
      toolbar: props.toolbar,
    });
    vaultInstanceRef.current = vault;

    // Set when the record list answered OBJECTS_UNAVAILABLE and the tab fell back to the bucket
    // listing: its rows are then legacy rows (no attachmentId), so remove behaves fully as a
    // legacy tab — bytes deleted, then the row found by its key — never leaving an orphan row.
    let bucketFallback = false;

    // The record's rows, plus the live listing of this folder (P10): bytes no row claims
    // (shown, never adopted) and where each row's bytes are. A listing failure must not empty
    // the tab: null, and the rows still show (none is then guessed missing).
    function loadRegistry() {
      return Promise.all([
        AttachmentService.listForRecord(props.source, props.id),
        AttachmentService.unregistered(props.source, props.id, props.path).catch((e) => {
          console.warn("Folder listing failed; record files listed without it: ", e);
          return null;
        }),
      ]);
    }

    // The bucket listing, as Vault has always listed (a tab without a record, and the
    // fallback below).
    function bucketListing() {
      return Storage.list(props.path).then((results) => {
        console.debug(results);
        // filters out sub folders
        const docs = results.filter((result) => {
          // ignore the folder object
          if (result.size === 0) {
            return false;
          }

          // compare the object prefix to the path prop
          if (result.key) {
            const keyPrefix =
              result.key.split("/").slice(0, -1).join("/") + "/";
            if (keyPrefix === props.path) {
              return true;
            }
          }

          // anything that doesn't exactly match the path will be filtered out
          return false;
        });

        console.debug(docs);

        const dataset = docs.map((obj) => {
          return {
            id: obj.key,
            name: new URL(obj.key!, "https://example.com").pathname
              .split("/")
              .pop(),
            size: obj.size,
            key: obj.key,
          };
        });

        console.debug(dataset);
        return dataset;
      });
    }

    vault.events.on(UploaderEvents.beforeUploadFile, function (file) {
      if (props.readOnly) {
        enqueueSnackbar("View only. You can't upload files.", {
          variant: "error",
        });
        return false;
      }
      console.log(file.id);

      let key = `${props.path}${file.name!.replace(/[^\w\d_\-.]+/gi, "")}`;

      const storageConfig = {
        contentDisposition: `attachment; filename="${file.name}"`,
        metadata: { name: file.name! },
      };

      Storage.put(key, file.file, storageConfig)
        .then((resp) => {
          console.log(resp);
          vault.events.fire(UploaderEvents.uploadFile, [file, resp]);
        })
        .catch((e) => {
          // The server refuses an upload the caller may not make on this record
          // (the module's edit permission, a pilot's own records only — H282).
          // Without this the row sat "in progress" forever with no word why.
          console.error(e);
          enqueueSnackbar(`${file.name} was not uploaded${uploadRefusal(e)}`, {
            variant: "error",
          });
          vault.data.update(file.id!, { status: "failed" });
        });

      vault.data.update(file.id!, { status: "inprogress" });
      return false;
    });

    vault.events.on(UploaderEvents.uploadFile, async function (file, extra) {
      console.log("The file is uploaded");
      // extra.key is where the server STORED it — a free image-2.jpg when image.jpg
      // was taken (H282) — so the row keeps that as filename and the picked name as
      // title. Delete below finds the row by the stored key.
      const legacyCreate = () =>
        AttachmentService.create(
          attachmentForUpload({
            displayName: file.name!,
            storedKey: extra!.key,
            source: props.source,
            id: props.id,
            access: props.access,
          })
        );

      try {
        let attachment: { id: number };
        if (registry) {
          try {
            // The file and its usage in one server commit (attachments spec §5.2).
            attachment = await AttachmentService.finalize({
              key: extra!.key,
              source_type: props.source,
              source_id: props.id,
              title: file.name!,
              access: props.access,
            });
          } catch (e) {
            // A tenant without version-37 (app/attachments/objects.py unavailable()): register
            // the old way. The bytes are stored already, so never re-upload. Any OTHER refusal
            // (403, 409) must not fall back: a legacy row on top would be a second row.
            if (!objectsUnavailable(e)) throw e;
            attachment = await legacyCreate();
          }
        } else {
          attachment = await legacyCreate();
        }
        vault.data.update(file.id!, {
          key: extra!.key,
          status: "uploaded",
          attachmentId: attachment.id,
          // A just-uploaded row never passes through rowForFile (its id is not in any cached
          // listing, so it would read as missing): it gets the same display pair here, the
          // escaped name for DHX's grid template (which prints `name`) and the raw one for
          // download, text, the tooltip and the list template (withListName). DHX's update
          // never re-derives value/extension, which is why list mode reads `fileName`.
          ...(registry
            ? { kind: "aeronet", fileName: file.name!, name: escapeName(file.name!) }
            : {}),
        });
      } catch (e) {
        console.error(e);
        // finalize suppresses the global toast, so its reason is said here (or never).
        enqueueSnackbar(
          `File uploaded but its attachment record failed${registry ? registryRefusal(e) : ""}`,
          { variant: "error" }
        );
        // Unregistered bytes now: no attachmentId, so removing the row deletes them.
        vault.data.update(file.id!, {
          key: extra?.key,
          status: "uploaded",
          ...(registry ? { kind: "aeronet", fileName: file.name!, name: escapeName(file.name!) } : {}),
        });
      }
    });

    vault.events.on("beforeRemove", async function (file) {
      if (props.readOnly) {
        enqueueSnackbar("View only. You can't delete files.", {
          variant: "error",
        });
        return false;
      }
      // DHX does not wait for this handler: it cancels only on a literal false, and an async
      // handler returns a Promise, so the row is gone (and afterRemove fired) before any
      // await below resolves. A refused registry remove therefore puts the row back where it
      // was. `file` (the picked File) is dropped: DHX's init would reset `name` from it,
      // undoing the escaping. Read before the first await, while the row is still there.
      const index = vault.data.getIndex(file.id);
      const putBack = () => vault.data.add({ ...file, file: undefined }, index);
      if (registry && !bucketFallback && file.attachmentId) {
        // Off THIS record only (spec §3.5): the file stays for any other record and for the
        // retention rule. No bytes are deleted from here. A missing file can still be removed.
        try {
          await AttachmentService.remove(file.attachmentId);
        } catch (e) {
          console.error(e);
          enqueueSnackbar(`${file.fileName ?? file.name} was not removed${registryRefusal(e)}`, {
            variant: "error",
          });
          putBack();
          return false;
        }
        return true;
      }
      // Never stored (a failed or unfinished upload): nothing to delete, so the row just goes.
      if (!file.key) return true;
      try {
        await Storage.remove(file.key!);
      } catch (e) {
        console.error(e);
        enqueueSnackbar(`${file.fileName ?? file.name} was not deleted${uploadRefusal(e)}`, {
          variant: "error",
        });
        // Unregistered bytes of a record tab: put the row back. A tab without a record (or one
        // that fell back to the bucket listing) keeps today's behaviour.
        if (registry && !bucketFallback) putBack();
        return false;
      }
      return true;
    });

    vault.events.on("afterRemove", async function (file) {
      // With a record nothing is left to do: a registered row came off the record in
      // beforeRemove, and unregistered bytes have no row of this record by definition. Never
      // the legacy hard delete here.
      if (registry && !bucketFallback) return;
      if (file.attachmentId) {
        AttachmentService.delete(file.attachmentId);
      } else if (props.source && file.key) {
        // A listed file's row is found by its STORED key, server-side (H282). The old
        // lookup matched `filename == <listed name>`, but rows kept the picked name
        // ("My receipt.pdf") while the key kept the sanitised one ("Myreceipt.pdf"),
        // so every such delete left an orphan row.
        AttachmentService.deleteForKey(props.source, props.id, file.key);
      }
    });

    vault.events.on("removeAll", async function () {
      if (props.readOnly) {
        enqueueSnackbar("View only. You can't delete files.", {
          variant: "error",
        });
        return false;
      }
      if (registry && !bucketFallback) {
        // DHX has emptied its dataset before this fires, so re-read; and THIS Vault's rows
        // only, as the list shows them: a record's files span all its folders (SOR: sors/,
        // sors_po/, rma/). Registered rows come off the record; unregistered bytes are deleted.
        let rows: VaultRow[];
        try {
          const [files, folder] = await loadRegistry();
          rows = vaultRows(files, folder, props.path);
        } catch (e) {
          console.error(e);
          enqueueSnackbar("The files were not removed", { variant: "error" });
          return;
        }
        const results = await Promise.allSettled(
          rows.map((row) =>
            row.attachmentId ? AttachmentService.remove(row.attachmentId) : Storage.remove(row.key!)
          )
        );
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed) {
          enqueueSnackbar(
            `${failed} of ${rows.length} ${rows.length === 1 ? "file was" : "files were"} not removed`,
            { variant: "error" }
          );
          // DHX emptied the tab before this ran: show what is still there.
          try {
            const [files, folder] = await loadRegistry();
            vault.data.parse(vaultRows(files, folder, props.path) as any);
          } catch (e) {
            console.error(e);
          }
        }
        return;
      }
      const objs = await Storage.list(props.path);
      await Promise.allSettled(objs.map((obj) => Storage.remove(obj.key!)));

      await AttachmentService.deleteAll(props.source, props.id);
    });

    // With a record: its rows from the database (links and files with no bytes in this folder
    // included). Should a list route ever answer OBJECTS_UNAVAILABLE (none does today), the
    // bucket listing as before. Without a record: the bucket listing, unchanged.
    const listing: Promise<object[]> = registry
      ? loadRegistry()
          .then(([files, folder]) => vaultRows(files, folder, props.path))
          .catch((e) => {
            if (!objectsUnavailable(e)) throw e;
            bucketFallback = true;
            return bucketListing();
          })
      : bucketListing();

    listing
      .then((dataset) => {
        setLoading(false);
        // @ts-ignore
        vault.data.parse(dataset);
      })
      .catch((error) => {
        console.error("Error loading documents: ", error);
        setLoading(false);
        // NO HARDCODED FALLBACK DATA HERE. This catch() used to inject four
        // invented aviation documents --- including
        // "Certificate_of_Airworthiness_2026.pdf" and an engine logbook entry
        // --- into the file list whenever the real document load FAILED, with
        // no product gate. Vault is a SHARED component (work orders, tool
        // requests and other Files tabs all mount it), so any S3 or auth
        // failure showed every tenant a list of airworthiness paperwork that
        // does not exist. This is the worst variant of the pattern: it fires
        // only on error, which is exactly when a user most needs the truth.
        //
        // Removed 2026-08-23. The list stays empty on failure. Never
        // reintroduce sample documents here.
        try {
          // @ts-ignore
          vault.data.parse([]);
        } catch (e) {
          // ignore parse errors
        }
      });

    vault.events.on("click", async function (id, e) {
      console.debug(id);
      console.debug(e);
      var file = vault.data.getItem(id);
      if (file.kind && file.kind !== "aeronet") {
        // A link (or, from phase 3, a file in the customer's own store): open it, never fetch it.
        if (typeof file.url === "string" && OPENABLE_LINK.test(file.url)) {
          window.open(file.url, "_blank", "noopener,noreferrer");
        } else {
          enqueueSnackbar("This link can't be opened", { variant: "warning" });
        }
        return;
      }
      if (file.missing) {
        enqueueSnackbar("This file is missing from storage", { variant: "warning" });
        return;
      }
      const result = await Storage.get(file.key, { download: true });
      console.debug(result);
      // { download: true } always resolves to GetResult (never the plain URL
      // string branch) — narrow rather than widen the shared type, so a future
      // caller that omits download: true still gets a compile error instead of
      // an `any`.
      if (typeof result !== "string") {
        downloadBlob(result.Body, file.fileName ?? file.name);
      }
    });

    // DHX's file-name tooltip (vault.js getContainersEvents: tooltip(item.name)) sets
    // textContent, so a row's HTML-escaped name would show "&amp;" on hover. For a row that
    // carries its raw name (`fileName`: every row this tab builds with a record), the native
    // title shows that instead, and DHX's handler below the host is not reached. Rows without
    // it keep DHX's tooltip. Capture phase on the host, removed with the widget.
    const rawNameTitle = (e: MouseEvent) => {
      const nameNode = (e.target as Element | null)?.closest?.(
        ".dhx_dataview-item--file-name, .dhx_list-item--name"
      );
      const itemId = nameNode?.closest("[data-dhx-id]")?.getAttribute("data-dhx-id");
      const item = itemId ? vault.data.getItem(itemId) : undefined;
      if (!nameNode || typeof item?.fileName !== "string") return;
      nameNode.setAttribute("title", shownName({ fileName: item.fileName, missing: item.missing }));
      e.stopPropagation();
    };
    if (registry) host.addEventListener("mouseover", rawNameTitle, true);

    // List mode's name (withListName). DHX's List reads config.template at each render.
    const listConfig = vault.list?.config;
    if (registry && listConfig && typeof listConfig.template === "function") {
      const baseTemplate = listConfig.template;
      listConfig.template = (item: any) => withListName(baseTemplate(item), item);
    }

    return () => {
      host.removeEventListener("mouseover", rawNameTitle, true);
      vaultInstanceRef.current = null;
      vault.destructor();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drag-and-drop (upstream 7d866e0d3, ANX-2581): native capture-phase handlers.
  // DHX's own drop can miss when the widget was laid out while hidden or
  // zero-sized; React's capture phase still receives the drop, and we hand the
  // files to the DHX uploader ourselves.
  const allowDrop = (e: DragEvent) => {
    if (props.readOnly) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragEnter = (e: DragEvent) => {
    allowDrop(e);
    if (!props.readOnly) setDragging(true);
  };

  const onDragOver = (e: DragEvent) => {
    allowDrop(e);
    if (!props.readOnly) setDragging(true);
  };

  const onDragLeave = (e: DragEvent) => {
    // Only clear when leaving the host, not when crossing child nodes.
    if (e.currentTarget === e.target) {
      setDragging(false);
    }
  };

  const onDrop = (e: DragEvent) => {
    allowDrop(e);
    setDragging(false);
    if (props.readOnly) {
      enqueueSnackbar("View only. You can't upload files.", {
        variant: "error",
      });
      return;
    }
    const vault = vaultInstanceRef.current;
    if (!vault?.uploader || !e.dataTransfer) return;
    // A link dragged in (from a browser's address bar, a page, SharePoint): kept on the record
    // exactly as Add link keeps it (attachments/addLink.ts). Only a tab that names its record
    // has one to keep it on; files dropped in still upload below.
    const link = registry ? droppedLink(e.dataTransfer) : null;
    if (link) {
      addLinkToRecord({ url: link, source: props.source, id: props.id })
        .then((row) => {
          vaultInstanceRef.current?.data.add(rowForFile(row, props.path, null));
          enqueueSnackbar("Link added", { variant: "success" });
        })
        .catch((err) => enqueueSnackbar(linkRefusal(err), { variant: "error" }));
      return;
    }
    try {
      vault.uploader.parseFiles(e.dataTransfer);
    } catch (err) {
      console.error("Vault drop parse failed:", err);
      enqueueSnackbar("Failed to accept dropped files", { variant: "error" });
    }
  };

  return (
    <>
      {registry && !props.readOnly && (
        // The Files tab's one record action besides upload. The shared compact toolbar
        // button (Buttons.tsx), outlined; the form is the right-hand Sheet.
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LinkIcon />}
            sx={listToolbarButtonSx}
            onClick={() => setLinkOpen(true)}
          >
            Add link
          </Button>
        </Box>
      )}
      <Box sx={{ position: "relative", width: "100%", height: 450 }}>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              bgcolor: "background.paper",
              p: 1,
            }}
          >
            <Skeleton
              animation="wave"
              variant="rectangular"
              width="100%"
              height={40}
              sx={{ marginBottom: 0.5 }}
            />
            <Skeleton
              animation="wave"
              variant="rectangular"
              width="100%"
              height={100}
            />
          </Box>
        )}
        <Box
          ref={vaultRef}
          className={`widget-box`}
          onDragEnterCapture={onDragEnter}
          onDragOverCapture={onDragOver}
          onDragLeaveCapture={onDragLeave}
          onDropCapture={onDrop}
          sx={{
            // FILL THE PANEL. The DHX widget root `.dhx_vault` declares only
            // `background-color` + `border` (vault.css) — no width and no height.
            // Its inner `.dhx_vault--container` is width/height:100% OF THAT
            // collapsed box, so the widget took its intrinsic content width and
            // `justifyContent: center` parked it as a narrow vertical sliver in
            // the middle of a wide empty panel (Jay, QA 2026-08-24 — reported on
            // the Company Files tab, but this component is the Files tab of ~30
            // modules, so it was every one of them).
            //
            // `stretch` + an explicit 100% on the widget root is the fix: the
            // container no longer centres an auto-width child, and the root now
            // has a definite box for `.dhx_vault--container` to fill.
            //
            // Keep the host in layout always — never `display: none` while DHX is
            // mounted: that zero-sizes the droppable and breaks drag-and-drop
            // (upstream 7d866e0d3). The loading skeleton overlays it instead.
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'stretch',
            width: '100%',
            height: '100%',
            backgroundColor:"white",
            outline: dragging ? (theme) => `2px dashed ${theme.palette.primary.main}` : 'none',
            outlineOffset: -2,
            '& .dhx_vault': {
              width: '100%',
              height: '100%',
              boxSizing: 'border-box',
            },
            '& .dhx_toolbar-button__icon': {
              '&:before': {
                fontSize: 30,
                lineHeight: 30,
              }
            },
            '& .dhx-dropable-area':{
              backgroundColor:"white"
            },
            '& .dhx_vault--toolbar .dhx_toolbar':{
              backgroundColor:"white"
            },
            '& .dhx_widget .dhx_toolbar-button__icon:before ':{
              fontSize:"30px"
            },
            ...(props.readOnly
              ? {
                  '& .dhx_dataview-item--remove-btn, & .dhx_item--btn-remove': {
                    display: 'none !important',
                  },
                }
              : {})
          }}
        >
        </Box>
      </Box>
      {registry && (
        <AddLinkSheet
          open={linkOpen}
          onClose={() => setLinkOpen(false)}
          source={props.source}
          id={props.id}
          // A link has no folder or bytes: nothing for a listing to decide, so no `present`.
          onAdded={(row) => vaultInstanceRef.current?.data.add(rowForFile(row, props.path, null))}
        />
      )}
    </>
  );
}

export default Vault;
